// A campaign draft: /growth/campaigns/new and /growth/campaigns/:id/edit.
//
// Only a draft comes here: once a campaign starts, its audience and emails are
// fixed, and the rest is edited in place on its own page.
//
// Who's in it is listed by name as the filters change (owner, 2026-11-03: "I
// should see what leads are in that filter"). Leads picked on the Leads page
// arrive as ?leads=1,2,3 (a new campaign) or are already on the draft's
// audience.lead_ids: only those, every filter still applying to them.

import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { AudiencePreview, Campaign, CampaignAudience, CampaignWrite, GrowthTemplate } from '@/api/growthCampaignTypes'
import { ACTIVITY_STATUSES, AUDIENCE_VERIFY, BUCKETS, LEAD_TYPES, type ActivityStatus, type AudienceVerify, type Bucket, type LeadStatus, type LeadType } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import { Button, ButtonLink, Card, CardHeader, EmptyState, ErrorState, FormField, InlineAlert, LoadingState, MultiSelect, PlusIcon, SelectInput, TextInput, TrashIcon } from '@/components/ui'
import { ACTIVITY_LABELS, BUCKET_LABELS, LEAD_STATUS_LABELS, LEAD_TYPE_LABELS, VERIFY_LABELS } from '@/lib/growth'
import { COMMON_TIME_ZONES } from '@/lib/growthCampaigns'
import { useDebouncedValue, useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'

const AUDIENCE_STATUSES: LeadStatus[] = ['new', 'researching', 'contacted']

interface StepDraft {
  template_id: number | ''
  delay_days: number
}

interface Draft {
  name: string
  statuses: LeadStatus[]
  activity: ActivityStatus[]
  verify: AudienceVerify[]
  types: LeadType[]
  state: string
  platform: string
  /** Ids as text, as a drop-down's values are. */
  platform_ids: string[]
  buckets: Bucket[]
  /** Picked by hand on the Leads page; empty means the filters choose. */
  lead_ids: number[]
  min_priority: string
  q: string
  steps: StepDraft[]
  starts_at: string
  daily_cap: string
  window_start: string
  window_end: string
  time_zone: string
}

/** datetime-local in the viewer's zone, from and to an instant. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** ?leads=1,2,3 from the Leads page's Add to campaign: whole positive numbers only. */
function pickedFrom(raw: string | null): number[] {
  if (!raw) return []
  return [...new Set(raw.split(',').map((s) => Number(s.trim())).filter((n) => Number.isInteger(n) && n > 0))].slice(0, 2000)
}

function fromCampaign(c: Campaign | null, picked: number[] = []): Draft {
  return {
    name: c?.name ?? '',
    statuses: c?.audience.statuses ?? ['new', 'researching'],
    activity: c?.audience.activity ?? ['active'],
    verify: c?.audience.verify ?? ['verified'],
    types: c?.audience.types ?? [],
    state: c?.audience.states?.join(', ') ?? '',
    platform: c?.audience.platforms?.join(', ') ?? '',
    platform_ids: c?.audience.platform_ids?.map(String) ?? [],
    buckets: c?.audience.buckets ?? [],
    lead_ids: c?.audience.lead_ids ?? picked,
    min_priority: c?.audience.min_priority ? String(c.audience.min_priority) : '',
    q: c?.audience.q ?? '',
    steps: c?.steps.length ? c.steps.map((s) => ({ template_id: s.template_id, delay_days: s.delay_days })) : [{ template_id: '', delay_days: 0 }],
    starts_at: toLocalInput(c?.starts_at ?? null),
    daily_cap: String(c?.daily_cap ?? 10),
    window_start: c?.window_start ?? '09:00',
    window_end: c?.window_end ?? '17:00',
    time_zone: c?.time_zone ?? 'America/New_York',
  }
}

const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

function toAudience(d: Draft): CampaignAudience {
  const out: CampaignAudience = { statuses: d.statuses, activity: d.activity, verify: d.verify }
  if (d.types.length) out.types = d.types
  if (list(d.state).length) out.states = list(d.state)
  if (list(d.platform).length) out.platforms = list(d.platform)
  if (d.platform_ids.length) out.platform_ids = d.platform_ids.map(Number)
  if (d.buckets.length) out.buckets = d.buckets
  if (d.lead_ids.length) out.lead_ids = d.lead_ids
  if (d.min_priority) out.min_priority = Number(d.min_priority)
  if (d.q.trim()) out.q = d.q.trim()
  return out
}

function toWrite(d: Draft): CampaignWrite {
  return {
    name: d.name.trim(),
    audience: toAudience(d),
    steps: d.steps.filter((s) => s.template_id !== '').map((s) => ({ template_id: Number(s.template_id), delay_days: s.delay_days })),
    starts_at: d.starts_at ? new Date(d.starts_at).toISOString() : null,
    daily_cap: Number(d.daily_cap),
    window_start: d.window_start,
    window_end: d.window_end,
    time_zone: d.time_zone.trim(),
  }
}

/** A drop-down of several choices, for one audience filter. Nothing chosen means `placeholder`. */
function Choices<T extends string>({ label, placeholder, values, options, labels, onChange }: { label: string; placeholder: string; values: T[]; options: readonly T[]; labels: Record<T, string>; onChange: (next: T[]) => void }) {
  return (
    <FormField label={label}>
      {({ id }) => (
        <MultiSelect
          id={id}
          label={label}
          placeholder={placeholder}
          values={values}
          options={options.map((o) => ({ value: o, label: labels[o] }))}
          onChange={(next) => onChange(next as T[])}
        />
      )}
    </FormField>
  )
}

function AudienceSummary({ audience }: { audience: CampaignAudience }) {
  const key = useDebouncedValue(JSON.stringify(audience), 300)
  const { data, error, status, refetch } = useQuery(['growth', 'audience-preview', key], (signal) => api.growth.campaigns.previewAudience(JSON.parse(key) as CampaignAudience, signal), { keepPrevious: true })
  if (status === 'error' && !data) return <ErrorState error={error} title="Couldn't count this audience" onRetry={refetch} />
  if (!data) return <p className="text-[13px] text-muted">Counting…</p>
  const { excluded } = data
  const rest = [
    excluded.no_email ? `${excluded.no_email} with no email` : null,
    excluded.not_us ? `${excluded.not_us} outside the US` : null,
    excluded.suppressed ? `${excluded.suppressed} on the do-not-contact list` : null,
    excluded.undeliverable ? `${excluded.undeliverable} on a test domain` : null,
    excluded.skip ? `${excluded.skip} in Skip` : null,
    excluded.needs_review ? `${excluded.needs_review} needing review` : null,
    excluded.unverified ? `${excluded.unverified} not verified` : null,
    excluded.failed ? `${excluded.failed} that failed verification` : null,
    excluded.stale ? `${excluded.stale} stale` : null,
    excluded.unknown ? `${excluded.unknown} with unknown activity` : null,
    excluded.active ? `${excluded.active} active` : null,
  ].filter(Boolean)
  const stale = data.included.stale
  const review = data.included_verify.needs_review
  return (
    <div className="grid gap-1.5 rounded-md bg-raised p-3" data-testid="audience-summary">
      <p className="text-[13.5px] font-semibold text-ink">
        {data.eligible} of {data.total} {data.total === 1 ? 'lead' : 'leads'} can be emailed
      </p>
      {rest.length ? <p className="text-xs text-muted">Left out: {rest.join(', ')}.</p> : null}
      {stale ? <p className="text-xs font-semibold text-warn">Includes {stale} stale {stale === 1 ? 'lead' : 'leads'}.</p> : null}
      {review ? <p className="text-xs font-semibold text-warn">Includes {review} {review === 1 ? 'lead that needs' : 'leads that need'} review.</p> : null}
      {data.sample.length ? <WhoList leads={data.sample} total={data.eligible} /> : null}
    </div>
  )
}

/** Who's in it, by name, in the order they'd be emailed. */
function WhoList({ leads, total }: { leads: AudiencePreview['sample']; total: number }) {
  const place = (l: AudiencePreview['sample'][number]) => [l.city, l.state].filter(Boolean).join(', ')
  return (
    <div className="mt-1 grid gap-1.5">
      <p className="text-xs font-semibold text-ink">{total > leads.length ? `The first ${leads.length} of ${total}, in the order they'd be emailed` : "Who's in it, in the order they'd be emailed"}</p>
      <ul aria-label="Leads in this campaign" className="max-h-80 divide-y divide-line overflow-y-auto rounded-md border border-line bg-panel">
        {leads.map((l) => (
          <li key={l.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2 text-[13px]">
            <span className="grid min-w-0 gap-0.5">
              <Link to={`/growth/leads/${l.id}`} className="truncate font-semibold text-ink hover:text-accent">
                {l.name}
              </Link>
              <span className="truncate font-mono text-xs text-muted">{l.email}</span>
            </span>
            <span className="text-right text-xs text-muted">
              {[place(l), l.platform].filter(Boolean).join(' · ') || '—'}
              {l.activity_status !== 'active' ? <span className="ml-1.5 font-semibold text-warn">{ACTIVITY_LABELS[l.activity_status]}</span> : null}
              {l.verify_status !== 'verified' ? <span className="ml-1.5 font-semibold text-warn">{VERIFY_LABELS[l.verify_status as AudienceVerify]}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Steps({ steps, templates, onChange }: { steps: StepDraft[]; templates: GrowthTemplate[]; onChange: (next: StepDraft[]) => void }) {
  const options = [{ value: '', label: 'Choose a template' }, ...templates.map((t) => ({ value: String(t.id), label: t.check.ok ? t.name : `${t.name} (needs work)` }))]
  const set = (i: number, patch: Partial<StepDraft>) => onChange(steps.map((s, x) => (x === i ? { ...s, ...patch } : s)))
  return (
    <div className="grid gap-3">
      {steps.map((step, i) => (
        <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_8rem_auto]">
          <FormField label={i === 0 ? 'First email' : `Follow-up ${i}`}>
            {({ id }) => <SelectInput id={id} value={String(step.template_id)} options={options} onChange={(e) => set(i, { template_id: e.target.value ? Number(e.target.value) : '' })} />}
          </FormField>
          {i > 0 ? (
            <FormField label="Days after">
              {({ id }) => <TextInput id={id} type="number" min={1} max={365} value={step.delay_days} onChange={(e) => set(i, { delay_days: Number(e.target.value) })} />}
            </FormField>
          ) : (
            <span className="hidden sm:block" />
          )}
          {i > 0 ? (
            <Button aria-label={`Remove follow-up ${i}`} icon={<TrashIcon size={15} />} onClick={() => onChange(steps.filter((_, x) => x !== i))} />
          ) : (
            <span className="hidden sm:block" />
          )}
        </div>
      ))}
      <p className="text-xs text-muted">A follow-up stops if they reply, unsubscribe or bounce.</p>
      {steps.length < 5 ? (
        <div>
          <Button size="sm" icon={<PlusIcon size={14} />} onClick={() => onChange([...steps, { template_id: '', delay_days: 3 }])}>
            Add a follow-up
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function Form({ campaign, templates }: { campaign: Campaign | null; templates: GrowthTemplate[] }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [draft, setDraft] = useState<Draft>(() => fromCampaign(campaign, campaign ? [] : pickedFrom(params.get('leads'))))
  const { data: platformList } = useQuery(['growth', 'platforms', 'all'], (signal) => api.growth.platforms.list({}, signal))
  // Only what has leads on it, or was chosen: a long list of empty platforms helps nobody.
  const platforms = (platformList?.platforms ?? []).filter((p) => p.lead_count > 0 || draft.platform_ids.includes(String(p.id)))
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setProblem(null)
    try {
      const saved = campaign ? await api.growth.campaigns.update(campaign.id, toWrite(draft)) : await api.growth.campaigns.create(toWrite(draft))
      invalidateQueries('growth')
      navigate(`/growth/campaigns/${saved.id}`)
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="grid grid-cols-1 gap-4">
      <Card>
        <FormField label="Name" hint="For you. Nobody who gets the email sees this.">
          {({ id }) => <TextInput id={id} value={draft.name} maxLength={120} required onChange={(e) => set({ name: e.target.value })} />}
        </FormField>
      </Card>

      <Card>
        <CardHeader title="Who" description="Chosen fresh each day, so a lead added later can still join. Only US leads with a public email are ever selected." />
        <div className="grid gap-4">
          {draft.lead_ids.length > 0 ? (
            <InlineAlert
              title={`Only the ${draft.lead_ids.length} ${draft.lead_ids.length === 1 ? 'lead' : 'leads'} you picked`}
              action={
                <Button size="sm" onClick={() => set({ lead_ids: [] })}>
                  Use the filters instead
                </Button>
              }
            >
              Picked on the Leads page. The filters below still apply to them, so a lead that isn't verified or active is left out unless you choose it.
            </InlineAlert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Choices label="Lead status" placeholder="Choose at least one" values={draft.statuses} options={AUDIENCE_STATUSES} labels={LEAD_STATUS_LABELS} onChange={(statuses) => set({ statuses })} />
            <Choices label="Verification" placeholder="Verified only" values={draft.verify} options={AUDIENCE_VERIFY} labels={VERIFY_LABELS} onChange={(verify) => set({ verify })} />
            <Choices label="Activity" placeholder="Active only" values={draft.activity} options={ACTIVITY_STATUSES} labels={ACTIVITY_LABELS} onChange={(activity) => set({ activity })} />
            <Choices label="Type" placeholder="Any type" values={draft.types} options={LEAD_TYPES} labels={LEAD_TYPE_LABELS} onChange={(types) => set({ types })} />
            <Choices label="Bucket" placeholder="All but Skip" values={draft.buckets} options={BUCKETS} labels={BUCKET_LABELS} onChange={(buckets) => set({ buckets })} />
            {platforms.length > 0 ? (
              <Choices
                label="Platform"
                placeholder="Any platform"
                values={draft.platform_ids}
                options={platforms.map((p) => String(p.id))}
                labels={Object.fromEntries(platforms.map((p) => [String(p.id), `${p.name} (${p.lead_count})`]))}
                onChange={(platform_ids) => set({ platform_ids })}
              />
            ) : null}
          </div>
          {draft.verify.includes('needs_review') ? <InlineAlert tone="warn">Leads that need review have something a person should look at first. They're in this campaign only because you chose them. A lead that failed can't be chosen.</InlineAlert> : null}
          {draft.activity.includes('stale') ? <InlineAlert tone="warn">Stale leads look finished: the newest evidence of a season is old. They're in this campaign only because you chose them.</InlineAlert> : null}
          {draft.buckets.includes('skip_enterprise') ? <InlineAlert tone="warn">Skip leads are in this campaign only because you chose them.</InlineAlert> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="State" hint="Comma separated. Empty means any.">
              {({ id }) => <TextInput id={id} value={draft.state} placeholder="FL, GA" onChange={(e) => set({ state: e.target.value })} />}
            </FormField>
            <FormField label="Platform as written" hint="For leads with no linked platform. Comma separated.">
              {({ id }) => <TextInput id={id} value={draft.platform} placeholder="RecLeague" onChange={(e) => set({ platform: e.target.value })} />}
            </FormField>
            <FormField label="Priority at least">
              {({ id }) => <TextInput id={id} type="number" min={0} max={100} value={draft.min_priority} onChange={(e) => set({ min_priority: e.target.value })} />}
            </FormField>
            <FormField label="Search">{({ id }) => <TextInput id={id} value={draft.q} maxLength={200} placeholder="Name, email or city" onChange={(e) => set({ q: e.target.value })} />}</FormField>
          </div>
          <AudienceSummary audience={toAudience(draft)} />
        </div>
      </Card>

      <Card>
        <CardHeader title="What" />
        {templates.length === 0 ? (
          <EmptyState title="No templates yet" description="Write an email first." action={<ButtonLink to="/growth/templates/new">New template</ButtonLink>} />
        ) : (
          <Steps steps={draft.steps} templates={templates} onChange={(steps) => set({ steps })} />
        )}
      </Card>

      <Card>
        <CardHeader title="When" description="Adding the campaign to the schedule is your approval of every email it plans, follow-ups too: you confirm who's in it, the emails, the start, the cap and the window then. Each day's emails are spread through the window, with a few minutes at random between each, and show on the Schedule, where you can remove or move one." />
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Start" hint="Empty means as soon as you start it.">
            {({ id }) => <TextInput id={id} type="datetime-local" value={draft.starts_at} onChange={(e) => set({ starts_at: e.target.value })} />}
          </FormField>
          <FormField label="Most per day" hint="Sending also has its own daily cap, across all campaigns.">
            {({ id }) => <TextInput id={id} type="number" min={1} max={2000} required value={draft.daily_cap} onChange={(e) => set({ daily_cap: e.target.value })} />}
          </FormField>
          <FormField label="Window opens">{({ id }) => <TextInput id={id} type="time" required value={draft.window_start} onChange={(e) => set({ window_start: e.target.value })} />}</FormField>
          <FormField label="Window closes">{({ id }) => <TextInput id={id} type="time" required value={draft.window_end} onChange={(e) => set({ window_end: e.target.value })} />}</FormField>
          <FormField className="sm:col-span-2" label="Time zone">
            {({ id }) => (
              <>
                <TextInput id={id} list="growth-time-zones" value={draft.time_zone} required onChange={(e) => set({ time_zone: e.target.value })} />
                <datalist id="growth-time-zones">
                  {COMMON_TIME_ZONES.map((z) => (
                    <option key={z} value={z} />
                  ))}
                </datalist>
              </>
            )}
          </FormField>
        </div>
      </Card>

      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      <div className="flex justify-end gap-2">
        <ButtonLink to={campaign ? `/growth/campaigns/${campaign.id}` : '/growth/campaigns'}>Cancel</ButtonLink>
        <Button type="submit" variant="primary" loading={saving}>
          Save draft
        </Button>
      </div>
    </form>
  )
}

export default function CampaignEditPage() {
  const { id: rawId } = useParams()
  const isNew = rawId === undefined
  const id = Number(rawId)
  const navigate = useNavigate()
  const campaign = useQuery(isNew ? null : ['growth', 'campaign', id], (signal) => api.growth.campaigns.get(id, signal))
  const templates = useQuery(['growth', 'templates'], (signal) => api.growth.templates.list(signal))
  useDocumentTitle(isNew ? 'New campaign' : (campaign.data?.name ?? 'Campaign'))

  const locked = campaign.data && campaign.data.status !== 'draft'
  useEffect(() => {
    if (locked) navigate(`/growth/campaigns/${id}`, { replace: true })
  }, [locked, id, navigate])

  const header = <PageHeader crumbs={[{ label: 'Campaigns', to: '/growth/campaigns' }, { label: campaign.data?.name ?? 'New campaign' }]} title={campaign.data?.name ?? 'New campaign'} />

  if (!isNew && campaign.status === 'error' && isApiError(campaign.error) && campaign.error.code === 'not_found') {
    return (
      <Card>
        <EmptyState title="Campaign not found" description="It may have been deleted." action={<ButtonLink to="/growth/campaigns">Back to campaigns</ButtonLink>} />
      </Card>
    )
  }
  if ((!isNew && !campaign.data) || !templates.data) {
    const failed = (!isNew && campaign.status === 'error' ? campaign : null) ?? (templates.status === 'error' ? templates : null)
    if (failed) {
      return (
        <Card>
          <ErrorState error={failed.error} title="Couldn't load this page" onRetry={failed.refetch} />
        </Card>
      )
    }
    return <LoadingState label="Loading…" />
  }
  return (
    <div className="grid grid-cols-1 gap-5">
      {header}
      <Form key={campaign.data?.id ?? 'new'} campaign={campaign.data ?? null} templates={templates.data} />
    </div>
  )
}
